import { useUser } from "../context/UserContext";

// Internal personas (BV staff, Zumale admins) administer all tenants, so the
// tenant's plan should not gate what they can see. For these personas
// hasFeature() returns true unconditionally — the FE renders the surface as
// enabled. BE still enforces the plan on actual mutations via require_feature.
const PERSONAS_BYPASS_PLAN = new Set(["BV_ASSOCIATE", "ZUMALE_USER"]);

const readDottedPath = (obj, path) => {
  if (!obj || !path) return undefined;
  let cursor = obj;
  for (const segment of path.split(".")) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = cursor[segment];
  }
  return cursor;
};

const useSubscriptionFeatures = () => {
  const { userInfo } = useUser();

  const persona = userInfo?.persona;
  const subscription = userInfo?.loginIdentifierDetails?.subscription;
  const features = subscription?.subscriptionType?.feature;

  /**
   * Returns true iff the tenant's plan unlocks the named feature.
   * Accepts a dotted path like "modules.crm". Fails closed when the
   * subscription / feature JSON is missing.
   *
   * Bypassed for internal personas (BV staff / Zumale admins).
   */
  const hasFeature = (featureName) => {
    if (PERSONAS_BYPASS_PLAN.has(persona)) return true;
    if (!features) return false;
    const value = readDottedPath(features, featureName);
    return value === true;
  };

  /**
   * Inverse helper for UI components that want to render disabled state.
   * Returns { disabled, reason } — `reason` is a short string suitable for
   * a tooltip.
   */
  const isFeatureDisabled = (featureName) => {
    if (PERSONAS_BYPASS_PLAN.has(persona)) return { disabled: false, reason: null };
    if (hasFeature(featureName)) return { disabled: false, reason: null };
    return {
      disabled: true,
      reason: "Available on BuildVizer Unified",
    };
  };

  const getSubscriptionType = () =>
    subscription?.subscriptionType?.typeName || null;

  const getSubscriptionEndDate = () => subscription?.endDate || null;

  return {
    hasFeature,
    isFeatureDisabled,
    getSubscriptionType,
    getSubscriptionEndDate,
  };
};

export default useSubscriptionFeatures;
